// Origo Brand Studio - client-side API surface.
// Server handlers live in services/gateway/internal/clienthandler/brand.go.

import { api } from './client';

export type BrandKind = 'intro' | 'outro' | 'graphic' | 'thumbnail';
export type BrandLayerType = 'text' | 'image' | 'color';

export interface BrandLayer {
  key: string;
  type: BrandLayerType;
  layer_index: number;
  asset_index?: number;
  default: string;
}

export interface BrandVariant {
  id: string;
  template_id: string;
  aspect: string;
  lottie_path: string;
  width: number;
  height: number;
  fps: number;
  duration_ms: number;
  still_frame: number;
  layers: BrandLayer[];
}

export interface BrandTemplate {
  id: string;
  client_id: string | null;
  kind: BrandKind;
  name: string;
  description?: string;
  status: string;
  variants: BrandVariant[];
  created_at?: string;
  updated_at?: string;
}

export interface BrandField {
  id?: string;
  key: string;
  type: BrandLayerType;
  label: string;
  source: string;
  mandatory: boolean;
  auto_fill: boolean;
  default_value: string;
  sort_order: number;
}

export interface BrandRender {
  id: string;
  template_id: string;
  variant_id: string;
  aspect: string;
  output_kind: 'mp4' | 'png' | 'jpg';
  status: 'queued' | 'running' | 'ready' | 'failed';
  cache_key: string;
  output_path?: string;
  size_bytes?: number;
  duration_ms?: number;
  error?: string;
  values: Record<string, string>;
  match_id?: string | null;
  reel_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface BrandDefaults {
  default_intro_template_id: string | null;
  default_outro_template_id: string | null;
  default_thumb_template_id: string | null;
  brand_accent_color: string | null;
}

export const listBrandTemplates = (kind?: BrandKind) => {
  const q = new URLSearchParams();
  if (kind) q.set('kind', kind);
  const suffix = q.toString();
  return api.get<BrandTemplate[]>(`/client/v1/brand/templates${suffix ? `?${suffix}` : ''}`);
};

export const getBrandTemplate = (id: string) =>
  api.get<{ template: BrandTemplate; fields: BrandField[] }>(`/client/v1/brand/templates/${id}`);

export const queueBrandRender = (body: {
  template_id: string;
  aspect: string;
  output_kind: 'mp4' | 'png' | 'jpg';
  values?: Record<string, string>;
  match_id?: string;
  reel_id?: string;
}) => api.post<{ render_id: string; cache_key: string; cached: boolean; output_path?: string }>(
  '/client/v1/brand/renders',
  body,
);

export const getBrandRender = (id: string) =>
  api.get<BrandRender>(`/client/v1/brand/renders/${id}`);

export const listBrandRenders = () =>
  api.get<BrandRender[]>('/client/v1/brand/renders');

export const getBrandDefaults = () =>
  api.get<BrandDefaults>('/client/v1/brand/defaults');

export const putBrandDefaults = (body: Partial<BrandDefaults>) =>
  api.put<void>('/client/v1/brand/defaults', body);

/** Public URL for a rendered output. Renderer writes under
 *  CLIP_STORAGE_ROOT/brand/... and streamserve exposes /streams/v1/... */
export function brandOutputURL(rel?: string | null): string | null {
  if (!rel) return null;
  const clean = rel.replace(/^\/+/, '');
  return `/streams/v1/${clean}`;
}
